import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const numberWheelRollSkill: Skill = {
    name: SKILL_NAMES.numberWheelRoll,
    kind: 'codeo',
    description:
        'Roll digits into place on vertical wheels, like an odometer or a departure board — for a ' +
        'figure that should feel mechanically counted rather than smoothly interpolated.',
    instructions: `# Number Wheel Roll

Each digit sits on its own vertical strip of 0–9 and the strip rolls until the right digit is in the window. Digits land left to right, so the number resolves the way a mechanical counter does. Compared with a smooth count-up it feels *counted* rather than *animated* — heavier, more physical, and better for a figure that took work to reach.

Use it where the number is a total or a tally. For a value the narration delivers in passing, \`${SKILL_NAMES.counterTickUp}\` is lighter and lands more precisely.

## Markup

One window per digit. Each strip repeats 0–9 **\`SPINS + 1\` times** — three runs at \`SPINS = 2\` — because the wheel travels past two full revolutions before landing, and a strip that only holds one run rolls off its own end onto blank. Fix the window height to exactly one digit's line height so only one digit shows.

\`\`\`html
<div id="odometer" style="position: absolute; left: 620px; top: 420px; display: flex; gap: 6px; font-family: var(--l5-font-0), monospace; font-size: 180px; font-weight: 800; line-height: 1; color: var(--l5-color-0, #101820);">
  <div class="digit-window" style="position: relative; width: 108px; height: 180px; overflow: hidden;">
    <div class="digit-strip" style="position: absolute; left: 0; top: 0; width: 100%; text-align: center;">0<br>1<br>2<br>3<br>4<br>5<br>6<br>7<br>8<br>9<br>0<br>1<br>2<br>3<br>4<br>5<br>6<br>7<br>8<br>9<br>0<br>1<br>2<br>3<br>4<br>5<br>6<br>7<br>8<br>9</div>
  </div>
  <div class="digit-window" style="position: relative; width: 108px; height: 180px; overflow: hidden;">
    <div class="digit-strip" style="position: absolute; left: 0; top: 0; width: 100%; text-align: center;">0<br>1<br>2<br>3<br>4<br>5<br>6<br>7<br>8<br>9<br>0<br>1<br>2<br>3<br>4<br>5<br>6<br>7<br>8<br>9<br>0<br>1<br>2<br>3<br>4<br>5<br>6<br>7<br>8<br>9</div>
  </div>
  <div class="digit-window" style="position: relative; width: 108px; height: 180px; overflow: hidden;">
    <div class="digit-strip" style="position: absolute; left: 0; top: 0; width: 100%; text-align: center;">0<br>1<br>2<br>3<br>4<br>5<br>6<br>7<br>8<br>9<br>0<br>1<br>2<br>3<br>4<br>5<br>6<br>7<br>8<br>9<br>0<br>1<br>2<br>3<br>4<br>5<br>6<br>7<br>8<br>9</div>
  </div>
</div>
\`\`\`

## Timeline code

Roll past a whole number of full revolutions before landing, so each wheel visibly spins rather than nudging to its digit.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const TARGET = '482';
const DIGIT_HEIGHT = 180;
const SPINS = 2;
const strips = document.querySelectorAll('.digit-strip');

gsap.set(strips, { y: 0 });

strips.forEach((strip, index) => {
    const value = Number(TARGET[index]);
    const distance = -(SPINS * 10 + value) * DIGIT_HEIGHT;
    tl.to(strip, {
        y: distance,
        duration: 1.1 + index * 0.16,
        ease: 'power3.out',
    }, 0.3);
});

window.__timelines = { main: tl };
\`\`\`

Each wheel runs slightly longer than the one to its left, so they settle left to right without needing separate start times — the same shape a real counter has.

## Adapt

- **Spin count**: 2 full revolutions reads as mechanical. 1 is a nudge; past 4 the wheel is a blur and the landing is all anyone sees. Changing \`SPINS\` changes the markup too — the strip always needs \`SPINS + 1\` runs of 0–9.
- **Split-flap instead**: swap the vertical roll for a two-panel flip per character and the register becomes a departure board — mechanical in a louder, more nostalgic way.
- **Letters**: strips of A–Z work identically, for a destination or a word that should feel selected from a set.
- **Separator**: a comma or currency mark sits *outside* the windows as a static element, so it never rolls.
- **Landing accent**: a short colour flash on each window as its wheel stops makes the resolution audible-looking. Keep it under 0.15s.
- **Easing**: \`power3.out\` decelerates into the digit. \`back.out(1.2)\` adds a small mechanical bounce, which suits a physical counter but overplays a digital one.

## Pitfalls

- The window height and \`DIGIT_HEIGHT\` must be identical, and the strip's line height must equal both, or digits sit half-visible in their windows.
- Use a monospace or tabular face and fix each window's width. A proportional font makes the wheels different widths and the number wobbles.
- The strips are \`y\`-tweened, so none may carry a CSS \`transform\`.
- The strip must run past its target — \`(SPINS * 10 + value)\` — not just to it, or nothing appears to spin.
- Count the digits against the travel. At \`SPINS = 2\` a wheel ends up \`24 * DIGIT_HEIGHT\` down, so a strip holding a single 0–9 run has run out 1800px earlier and every window lands on blank. Nothing lints this — the composition simply renders empty windows.
- Three or four digits is the ceiling. Six wheels rolling at once is noise, and nobody reads the number anyway.
- Don't roll a number the narration is delivering at a precise moment; the landing is spread across the wheels and cannot hit a single onset cleanly.
- The frame is completely still once the wheels stop — see \`${SKILL_NAMES.ambientFloatLoop}\` for what carries the rest of the scene.`,
};
